import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {preload as preloadResource} from 'react-dom';
import Ratio from 'react-ratio';
import cn from 'classnames';

import ViewportMgr from 'coreClasses/view/ViewportMgr';
import Skeleton from 'coreComponents/Skeleton';

import {useHeaderCtx} from 'brandComponents/Header/HeaderCtx';
import {AVAILABLE_DPIS} from 'brandConstants';

import {IPictureProps} from './IPicture';

import styles from './Picture.module.scss';

/**
 * A `<source>`: adjacent breakpoints with the same images share one, so the media rules stay disjoint. Its own
 * `width`/`height`: with a different image per breakpoint (CMS) the space reserved is the one of that image.
 */
type TSource = {
	breakpoints: string[];
	srcSet: string;
	minWidth: number;
	maxWidth: number | null;
	isPriority: boolean;
	width: string | number;
	height: string | number;
};

const getMedia = ({minWidth, maxWidth}: TSource) =>
	[`(min-width:${minWidth}px)`, ...(maxWidth === null ? [] : [`(max-width:${maxWidth}px)`])].join(' and ');

const Picture = (
	{
		image,
		classNames,
		onImageLoaded,
		onClick,
		ratio,
		showSkeleton = false,
		noResize = false,
		index,
		priority = false,
		showPlaceholder = true,
		showOnlyLowQuality = false,
		lazy = true,
		threshold = 0.2,
		isObserverActive = true,
		printTitle = true,
		ref,
	}: IPictureProps,
) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [isObserved, setIsObserved] = useState(false);
	const [isMounted, setIsMounted] = useState(false);
	const pictureRef = useRef(null);
	const breakpointsMap = ViewportMgr.getBreakpointsMap(true);
	const headerCtx = useHeaderCtx(true);
	const localShowPlaceholder = headerCtx?.isInHeaderContext || showPlaceholder;
	const isPriority = priority === true || (Array.isArray(priority) && priority.length > 0);
	// A priority image is the LCP candidate: the real image from the server HTML, never a placeholder.
	const showRealImage = isPriority || isObserved || !isObserverActive || !localShowPlaceholder;

	const getImageUrlByBreakpoint = useCallback(
		(breakPoint: string = null, dpi: number = 1) => {
			if (noResize || breakPoint === null) {
				return image.getUrl();
			}

			return showRealImage ? image.getUrl(breakPoint, dpi) : image.getPlaceholderUrl(breakPoint, dpi);
		},
		[noResize, image, showRealImage],
	);

	const sources = useMemo(() => {
		const entries = Object.entries(breakpointsMap || {});

		return entries.reduce<TSource[]>((list, [breakpoint, minWidth], i) => {
			const nextMinWidth = entries[i + 1]?.[1];
			const maxWidth = nextMinWidth === undefined ? null : nextMinWidth - 1;
			const srcSet = AVAILABLE_DPIS.map((dpi) => ({url: getImageUrlByBreakpoint(breakpoint, dpi), dpi}))
				// Same URL at a higher density (no resize): the lower density is enough.
				.filter(({url}, j, all) => j === 0 || url !== all[j - 1].url)
				.map(({url, dpi}) => `${url} ${dpi}x`)
				.join(', ');
			const isBreakpointPriority = priority === true || (Array.isArray(priority) && priority.includes(breakpoint as never));
			const last = list[list.length - 1];

			if (last && last.srcSet === srcSet && last.isPriority === isBreakpointPriority) {
				last.breakpoints.push(breakpoint);
				last.maxWidth = maxWidth;
			} else {
				list.push({
					breakpoints: [breakpoint],
					srcSet,
					minWidth,
					maxWidth,
					isPriority: isBreakpointPriority,
					width: image.getWidth(breakpoint),
					height: image.getHeight(breakpoint),
				});
			}

			return list;
		}, []);
	}, [breakpointsMap, getImageUrlByBreakpoint, priority, image]);

	const imageUrl = getImageUrlByBreakpoint();

	if (isPriority && imageUrl) {
		// In the server HTML `<head>`: the browser fetches the image of the current viewport before the CSS and
		// the scripts, with high priority. One preload per `<source>` (disjoint media), only where it is a priority.
		sources
			.filter((source) => source.isPriority)
			.forEach((source) =>
				preloadResource(source.srcSet.split(' ')[0], {
					as: 'image',
					imageSrcSet: source.srcSet,
					media: getMedia(source),
					fetchPriority: 'high',
				}),
			);
	}

	useEffect(() => {
		setIsMounted(true);
	}, []);

	useEffect(() => {
		if (!showOnlyLowQuality) {
			const handleIntersection = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
				entries.forEach((e) => {
					if (e.isIntersecting) {
						setIsObserved(true);
						observer.unobserve(e.target);
					}
				});
			};

			const observer = new IntersectionObserver(handleIntersection, {
				rootMargin: '0px',
				threshold,
			});

			if (pictureRef.current) {
				observer.observe(pictureRef.current);
			}

			return () => {
				if (pictureRef.current) {
					observer.unobserve(pictureRef.current);
				}
			};
		} else {
			setIsObserved(true);
		}
	}, []);

	const handleOnLoad = useCallback(() => {
		setIsLoaded(true);

		if (typeof onImageLoaded === 'function') {
			onImageLoaded();
		}
	}, [onImageLoaded]);

	// `priority` on some breakpoints only: the image is hidden on the others (another one is shown there), where
	// a lazy image is not fetched at all; where it is shown, the preload has already fetched it.
	const loading = priority === true || (!lazy && !Array.isArray(priority)) ? 'eager' : 'lazy';

	const renderImage = useMemo(() => {
		return (
			<picture ref={pictureRef} className={cn(classNames, styles['general-picture'], 'general-picture', {loaded: isLoaded})}>
				{imageUrl && (
					<>
						{isMounted && !isLoaded && !isObserved && showSkeleton && !isPriority ? <Skeleton /> : null}

						{sources.map((source) => (
							<source
								key={source.breakpoints[0]}
								media={getMedia(source)}
								srcSet={source.srcSet}
								width={source.width || undefined}
								height={source.height || undefined}
							/>
						))}

						<img
							ref={ref}
							onLoad={handleOnLoad}
							onClick={onClick}
							loading={loading}
							fetchPriority={isPriority ? 'high' : undefined}
							src={getImageUrlByBreakpoint('xs', 1)}
							srcSet={AVAILABLE_DPIS.map((r) => `${getImageUrlByBreakpoint('xs', r)} ${r}x`).join(', ')}
							alt={image.getAlt()}
							title={printTitle ? image.getTitle() : undefined}
							width={image.getWidth()}
							height={image.getHeight()}
							data-index={index}
						/>
					</>
				)}
			</picture>
		);
	}, [
		classNames,
		isLoaded,
		imageUrl,
		isMounted,
		isObserved,
		showSkeleton,
		isPriority,
		sources,
		ref,
		handleOnLoad,
		onClick,
		loading,
		getImageUrlByBreakpoint,
		image,
		printTitle,
		index,
	]);

	if (ratio) {
		return <Ratio ratio={ratio}>{renderImage}</Ratio>;
	}

	return renderImage;
};

export default Picture;
